/**
 * CostBreakdownChart Component
 *
 * Displays cost breakdown using recharts (bar and pie charts)
 * Follows: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useMemo, useState } from 'react';
import { Card } from 'flowbite-react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import { formatCurrency } from '../api/costs';

export type ChartType = 'bar' | 'pie' | 'line';
export type BreakdownType = 'project' | 'company' | 'model' | 'time';

export interface ChartDataPoint {
  name: string;
  value: number;
  budget?: number;
  executions?: number;
  percentage?: number;
  date?: string;
}

export interface CostBreakdownChartProps {
  title: string;
  data: ChartDataPoint[];
  chartType?: ChartType;
  breakdownType?: BreakdownType;
  showLegend?: boolean;
  height?: number;
  isLoading?: boolean;
}

// Color palette for charts
const COLORS = [
  '#3b82f6', // blue-500
  '#10b981', // green-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#ec4899', // pink-500
  '#14b8a6', // teal-500
  '#f97316', // orange-500
  '#6366f1', // indigo-500
  '#84cc16', // lime-500
];

/**
 * Custom tooltip for charts
 */
const CustomTooltip = ({ active, payload, breakdownType }: any) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;

  return (
    <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
      <p className="font-semibold text-gray-900 mb-2">{data.name}</p>
      <div className="space-y-1 text-sm">
        <p className="text-gray-700">
          <span className="font-medium">Cost:</span> {formatCurrency(data.value)}
        </p>
        {data.budget !== undefined && (
          <p className="text-gray-700">
            <span className="font-medium">Budget:</span> {formatCurrency(data.budget)}
          </p>
        )}
        {data.executions !== undefined && (
          <p className="text-gray-700">
            <span className="font-medium">Executions:</span> {data.executions.toLocaleString()}
          </p>
        )}
        {data.percentage !== undefined && (
          <p className="text-gray-700">
            <span className="font-medium">Percentage:</span> {data.percentage.toFixed(1)}%
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * Custom label for pie chart
 */
const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent
}: any) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  if (percent < 0.05) return null; // Don't show label if less than 5%

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      className="text-xs font-semibold"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export const CostBreakdownChart: React.FC<CostBreakdownChartProps> = ({
  title,
  data,
  chartType = 'bar',
  breakdownType = 'project',
  showLegend = true,
  height = 400,
  isLoading = false
}) => {
  const [activeChartType, setActiveChartType] = useState<ChartType>(chartType);

  // Sort data by value descending
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => b.value - a.value);
  }, [data]);

  // Limit data to top 10 for better visibility
  const chartData = useMemo(() => {
    if (sortedData.length <= 10) return sortedData;

    const top10 = sortedData.slice(0, 10);
    const others = sortedData.slice(10);

    if (others.length > 0) {
      const othersTotal = others.reduce((sum, item) => sum + item.value, 0);
      const othersExecutions = others.reduce((sum, item) => sum + (item.executions || 0), 0);
      const othersPercentage = (othersTotal / sortedData.reduce((sum, item) => sum + item.value, 0)) * 100;

      top10.push({
        name: `Others (${others.length})`,
        value: othersTotal,
        executions: othersExecutions,
        percentage: othersPercentage
      });
    }

    return top10;
  }, [sortedData]);

  if (isLoading) {
    return (
      <Card>
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-64 text-gray-500">
          No cost data available
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="space-y-4">
        {/* Header with chart type switcher */}
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <div className="flex gap-2">
            <button
              onClick={() => setActiveChartType('bar')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                activeChartType === 'bar'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              aria-label="Show bar chart"
            >
              Bar
            </button>
            <button
              onClick={() => setActiveChartType('pie')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                activeChartType === 'pie'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              aria-label="Show pie chart"
            >
              Pie
            </button>
            {breakdownType === 'time' && (
              <button
                onClick={() => setActiveChartType('line')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  activeChartType === 'line'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                aria-label="Show line chart"
              >
                Line
              </button>
            )}
          </div>
        </div>

        {/* Chart */}
        <div style={{ width: '100%', height }}>
          <ResponsiveContainer>
            {activeChartType === 'bar' ? (
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip content={<CustomTooltip breakdownType={breakdownType} />} />
                {showLegend && <Legend />}
                <Bar dataKey="value" fill="#3b82f6" name="Cost" radius={[8, 8, 0, 0]} />
                {chartData[0]?.budget !== undefined && (
                  <Bar dataKey="budget" fill="#10b981" name="Budget" radius={[8, 8, 0, 0]} />
                )}
              </BarChart>
            ) : activeChartType === 'pie' ? (
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={150}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip breakdownType={breakdownType} />} />
                {showLegend && <Legend />}
              </PieChart>
            ) : activeChartType === 'line' && breakdownType === 'time' ? (
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip content={<CustomTooltip breakdownType={breakdownType} />} />
                {showLegend && <Legend />}
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Cost"
                />
                {chartData[0]?.budget !== undefined && (
                  <Line
                    type="monotone"
                    dataKey="budget"
                    stroke="#10b981"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ r: 4 }}
                    name="Budget"
                  />
                )}
              </LineChart>
            ) : (
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip breakdownType={breakdownType} />} />
                {showLegend && <Legend />}
                <Bar dataKey="value" fill="#3b82f6" name="Cost" radius={[8, 8, 0, 0]} />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
          <div className="text-center">
            <p className="text-sm text-gray-600">Total Cost</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatCurrency(data.reduce((sum, item) => sum + item.value, 0))}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Items</p>
            <p className="text-lg font-semibold text-gray-900">
              {data.length}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Avg Cost</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatCurrency(data.reduce((sum, item) => sum + item.value, 0) / data.length)}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default CostBreakdownChart;
